import {
  useGetAvigilonVideoConnectorDetailsQuery,
  useSetupAvigilonVideoConnectorMutation,
} from 'apollo/generated/client-operations';
import type { ConnectorDetailsDrawerProps } from 'containers/jobsiteConfiguration/tabs/connectors/drawers/connectorCatalogEntry';
import {
  ConnectorDetailsDrawerLayout,
  ConnectorDetailsDrawerQueryState,
  type ConnectorConnectionRecoveryAction,
} from 'containers/jobsiteConfiguration/tabs/connectors/drawers/shared/ConnectorDetailsDrawerLayout';
import React from 'react';

export function AvigilonVideoConnectorDetailsDrawer({
  connector,
  open,
  onClose,
  onAfterLeave,
  onUpdated,
}: ConnectorDetailsDrawerProps): React.ReactElement {
  const { definition } = connector;
  // Keep details available while the drawer exits so its transition can finish.
  const detailsQuery = useGetAvigilonVideoConnectorDetailsQuery({
    variables: { connectorId: connector.id },
    fetchPolicy: 'no-cache',
  });
  const videoConnector = detailsQuery.data?.avigilonVideoConnector ?? null;
  const [retrySetup, retrySetupState] =
    useSetupAvigilonVideoConnectorMutation();

  if (!videoConnector)
    return (
      <ConnectorDetailsDrawerQueryState
        open={open}
        title={definition.displayName}
        onClose={onClose}
        onAfterLeave={onAfterLeave}
        loading={detailsQuery.loading}
        error={
          detailsQuery.error ??
          (!detailsQuery.loading
            ? 'Avigilon Video connector details were not returned'
            : undefined)
        }
      />
    );

  const connectionRecovery: ConnectorConnectionRecoveryAction = {
    loading: retrySetupState.loading,
    execute: () =>
      retrySetup({ variables: { input: { siteId: videoConnector.siteId } } }),
    successTitle: 'Avigilon Video connected',
    failureTitle: 'Could not connect Avigilon Video',
  };

  return (
    <ConnectorDetailsDrawerLayout
      connector={videoConnector}
      open={open}
      onClose={onClose}
      onAfterLeave={onAfterLeave}
      onUpdated={onUpdated}
      onRefreshDetails={detailsQuery.refetch}
      connectionRecovery={connectionRecovery}
      presentation={{
        title: definition.displayName,
        description: definition.connectedDescription,
        statusMessages: {
          draft: 'Avigilon Video has not been connected.',
          disabled:
            'Avigilon Video is disabled. Enable it to keep the Preview connector active.',
          connectionFailed:
            'Avigilon Video could not be connected. Try connecting the connector again.',
          connecting: 'Avigilon Video is connecting.',
          connected: 'Avigilon Video is connected and ready to enable.',
          enabling: 'Avigilon Video is being enabled.',
          enablementFailed: 'Avigilon Video could not be enabled.',
        },
      }}
    />
  );
}
